import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Skeleton,
  Grid,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, PanelSkeleton } from '../../components/layout/Skeletons';
import OpeningHoursEditor from '../../components/pro/OpeningHoursEditor';
import { useMyListing } from '../../hooks/pro/useMyListing';

const STATUS_CHIPS = {
  DRAFT: { label: 'Brouillon', color: 'default' },
  PENDING: { label: 'En attente de validation', color: 'info' },
  PUBLISHED: { label: 'Publiée', color: 'success' },
  REJECTED: { label: 'À corriger', color: 'error' },
  ARCHIVED: { label: 'Archivée', color: 'warning' },
};

/**
 * A named part of the form.
 *
 * This replaces a `Divider` with small capitals running through it — a shape
 * that looks like a separator first and a title second, and set in capitals
 * on top, which is the hardest way to read four words.
 */
function SectionTitle({ children, hint }) {
  return (
    <Grid item xs={12}>
      <Box sx={{ pt: 1.5 }}>
        <Typography variant="h3" component="h2">
          {children}
        </Typography>
        {hint && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

/**
 * What the professional sees at each stage, so they always know whether
 * anything is expected of them.
 */
function StatusBanner({ listing, onSubmit, canSubmit, saving }) {
  if (!listing) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Votre fiche n'existe pas encore</AlertTitle>
        Remplissez le formulaire ci-dessous et enregistrez-le. Vous pourrez ensuite l'envoyer à
        la validation : rien n'est visible par les habitants avant.
      </Alert>
    );
  }

  if ('REJECTED' === listing.status) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>Votre fiche demande une correction</AlertTitle>
        {listing.rejection_reason}
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<SendIcon />}
            onClick={onSubmit}
            disabled={saving || !canSubmit}
          >
            Renvoyer à la validation
          </Button>
        </Box>
      </Alert>
    );
  }

  if ('PENDING' === listing.status) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>En attente de validation</AlertTitle>
        Nous relisons votre fiche. Vous recevrez un email dès qu'elle est publiée ou si une
        correction est nécessaire.
      </Alert>
    );
  }

  if ('PUBLISHED' === listing.status) {
    return (
      <Alert severity="success" sx={{ mb: 3 }}>
        <AlertTitle>Votre fiche est en ligne</AlertTitle>
        Les habitants peuvent vous trouver dans l'annuaire.
        <Box sx={{ mt: 1 }}>
          <Link component={RouterLink} to={`/prestataires/${listing.slug}`} underline="hover">
            Voir ma fiche telle qu'elle apparaît
          </Link>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
          Toute modification repasse par une validation avant d'être publiée.
        </Typography>
      </Alert>
    );
  }

  return (
    <Alert severity="warning" sx={{ mb: 3 }}>
      <AlertTitle>Brouillon</AlertTitle>
      Votre fiche est enregistrée mais invisible. Envoyez-la à la validation quand elle est
      complète.
      <Box sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={onSubmit}
          disabled={saving || !canSubmit}
        >
          Envoyer à la validation
        </Button>
      </Box>
    </Alert>
  );
}

function MyListing() {
  const {
    listing,
    formData,
    categories,
    cities,
    choosesCity,
    neighborhoods,
    loading,
    saving,
    error,
    fieldErrors,
    notice,
    handleInputChange,
    setOpeningHours,
    save,
    submit,
    canSubmit,
  } = useMyListing();

  if (loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement de votre fiche">
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 3.5 }} />
            <PanelSkeleton height={520} />
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  const statusChip = listing ? STATUS_CHIPS[listing.status] : null;

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Ma fiche professionnelle"
          lede="Décrivez votre activité pour les habitants du quartier."
          action={statusChip && <Chip label={statusChip.label} color={statusChip.color} />}
        />

        <StatusBanner
          listing={listing}
          onSubmit={submit}
          canSubmit={canSubmit}
          saving={saving}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {notice && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {notice}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
          <Box component="form" onSubmit={save} noValidate>
            <Grid container spacing={2.5}>
              {/* What kind of business this is decides what the listing shows
                  residents: an appointment form, or opening hours and offers. */}
              <Grid item xs={12}>
                <TextField
                  select
                  label="Type de fiche"
                  id="kind"
                  name="kind"
                  value={formData.kind}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!fieldErrors.kind}
                  helperText={
                    fieldErrors.kind ||
                    ('COMMERCE' === formData.kind
                      ? 'Les habitants verront vos horaires et vos offres, pas de prise de rendez-vous.'
                      : 'Les habitants pourront vous demander un rendez-vous.')
                  }
                  fullWidth
                >
                  <MenuItem value="SERVICE">Prestataire de services</MenuItem>
                  <MenuItem value="COMMERCE">Commerce</MenuItem>
                </TextField>
              </Grid>

              {'COMMERCE' === formData.kind && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                    Horaires d'ouverture
                  </Typography>
                  <OpeningHoursEditor
                    value={formData.openingHours}
                    onChange={setOpeningHours}
                    disabled={saving}
                    error={fieldErrors.openingHours}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nom de l'activité"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Plomberie Martin"
                  required
                  disabled={saving}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Catégorie"
                  id="categoryUuid"
                  name="categoryUuid"
                  value={formData.categoryUuid}
                  onChange={handleInputChange}
                  required
                  disabled={saving}
                  error={!!fieldErrors.categoryUuid}
                  helperText={fieldErrors.categoryUuid}
                  fullWidth
                >
                  <MenuItem value="">Choisir une catégorie</MenuItem>
                  {categories.map((category) => (
                    <MenuItem
                      key={category.uuid}
                      value={category.uuid}
                      sx={category.depth > 0 ? { pl: 3.5 } : { fontWeight: 600 }}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Ce que vous faites, votre zone d'intervention, vos délais."
                  disabled={saving}
                  error={!!fieldErrors.description}
                  helperText={fieldErrors.description}
                  multiline
                  minRows={4}
                  fullWidth
                />
              </Grid>

              {choosesCity && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Ville de l’activité"
                    id="citySlug"
                    name="citySlug"
                    value={formData.citySlug}
                    onChange={handleInputChange}
                    disabled={saving}
                    error={!!fieldErrors.citySlug}
                    helperText={fieldErrors.citySlug || 'Là où vous exercez, pas forcément là où vous habitez.'}
                    fullWidth
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.slug} value={city.slug}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Quartier"
                  id="neighborhoodUuid"
                  name="neighborhoodUuid"
                  // Blank until the chosen city's neighbourhoods have arrived.
                  value={neighborhoods.some((one) => one.uuid === formData.neighborhoodUuid) ? formData.neighborhoodUuid : ''}
                  onChange={handleInputChange}
                  disabled={saving || 0 === neighborhoods.length}
                  fullWidth
                >
                  <MenuItem value="">Aucun quartier précis</MenuItem>
                  {neighborhoods.map((neighborhood) => (
                    <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                      {neighborhood.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <SectionTitle hint="Sous quel statut vous exercez. Les habitants le voient sur votre fiche ; la raison sociale et le SIRET, non.">
                Votre statut
              </SectionTitle>

              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  label="Vous exercez comme"
                  name="legalStatus"
                  value={formData.legalStatus}
                  onChange={handleInputChange}
                  error={Boolean(fieldErrors.legalStatus)}
                  helperText={
                    fieldErrors.legalStatus ||
                    ('INDIVIDUAL' === formData.legalStatus
                      ? 'Le paiement se fait de particulier à particulier (CESU, chèque emploi service).'
                      : 'Entreprise, auto-entrepreneur, association.')
                  }
                  fullWidth
                  // A shopfront is always a business, so there is nothing to
                  // choose: the model refuses the other pairing.
                  disabled={saving || 'COMMERCE' === formData.kind}
                >
                  <MenuItem value="COMPANY">Une entreprise</MenuItem>
                  <MenuItem value="INDIVIDUAL">Un particulier</MenuItem>
                </TextField>
              </Grid>

              {'COMPANY' === formData.legalStatus && (
                <>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      label="Raison sociale"
                      name="legalName"
                      value={formData.legalName}
                      onChange={handleInputChange}
                      error={Boolean(fieldErrors.legalName)}
                      helperText={fieldErrors.legalName || 'Non publiée : elle sert à la modération'}
                      fullWidth
                      disabled={saving}
                      inputProps={{ maxLength: 180 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="SIRET"
                      name="siret"
                      value={formData.siret}
                      onChange={handleInputChange}
                      error={Boolean(fieldErrors.siret)}
                      helperText={fieldErrors.siret || '14 chiffres'}
                      fullWidth
                      disabled={saving}
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                </>
              )}

              <SectionTitle hint="Facultatif : vous pourrez les renseigner plus tard.">
                Comment vous joindre
              </SectionTitle>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Téléphone"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="02 35 22 10 10"
                  disabled={saving}
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Email de contact"
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="contact@exemple.fr"
                  disabled={saving}
                  error={!!fieldErrors.contactEmail}
                  helperText={fieldErrors.contactEmail}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Site web"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://exemple.fr"
                  disabled={saving}
                  error={!!fieldErrors.website}
                  helperText={fieldErrors.website}
                  fullWidth
                />
              </Grid>

              <SectionTitle hint="Facultative : laissez-la vide si vous intervenez uniquement à domicile.">
                Votre adresse
              </SectionTitle>

              <Grid item xs={12} sm={8}>
                <TextField
                  label="Rue"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="12 rue de Paris"
                  disabled={saving}
                  error={!!fieldErrors.street}
                  helperText={fieldErrors.street}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Code postal"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="76600"
                  disabled={saving}
                  error={!!fieldErrors.postalCode}
                  helperText={fieldErrors.postalCode}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'surface.rule',
                flexWrap: 'wrap',
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
              >
                Enregistrer
              </Button>

              {listing && 'PUBLISHED' === listing.status && (
                <Button
                  component={RouterLink}
                  to={`/prestataires/${listing.slug}`}
                  variant="outlined"
                  size="large"
                  startIcon={<VisibilityIcon />}
                >
                  Voir ma fiche
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default MyListing;
